import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import nl from 'date-fns/locale/nl';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import axiosInstance from '../../api/axiosConfig.jsx';
import NewReservationModal from './NewReservationModal.jsx';
import EditReservationModal from './EditReservationModal.jsx';
import { useSSE } from '../../Settings/SSEProvider.jsx';
import { jwtDecode } from "jwt-decode";
import 'bootstrap/dist/css/bootstrap.min.css';
import './Reservation.css';

const locales = {
    'nl': nl,
};

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
    getDay,
    locales,
});

const Reservations = () => {
    const { reservations } = useSSE();
    const [selectedReservation, setSelectedReservation] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [newReservationModalOpen, setNewReservationModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [timeSlots, setTimeSlots] = useState([]);
    const [companyName, setCompanyName] = useState('');

    useEffect(() => {
        const token = sessionStorage.getItem('authToken');
        const decodedToken = jwtDecode(token);
        const fetchedCompanyName = decodedToken.companyName || 'Onbekend';
        setCompanyName(fetchedCompanyName);
        fetchTimeSlots();
    }, []);

    const fetchTimeSlots = async () => {
        const token = sessionStorage.getItem('authToken');
        if (!token) {
            console.error('No auth token found');
            return;
        }

        try {
            const response = await axiosInstance.get('/timeslots')
            const openTimeSlots = response.data.filter(slot => slot.isOpen);
            openTimeSlots.sort((a, b) => a.startTime.localeCompare(b.startTime));

            const formattedTimeSlots = openTimeSlots.map(slot => ({
                ...slot,
                startTime: format(parse(slot.startTime, 'HH:mm:ss', new Date()), 'HH:mm'),
                endTime: format(parse(slot.endTime, 'HH:mm:ss', new Date()), 'HH:mm'),
            }));

            setTimeSlots(formattedTimeSlots);
        } catch (error) {
            console.error('Error fetching time slots:', error);
        }
    };

    const formatReservations = (reservations) => {
        return reservations.map(reservation => {
            const start = new Date(`${reservation.data.reservationDate}T${reservation.data.reservationTime}`);
            const end = new Date(start);
            end.setHours(end.getHours() + 1);

            return {
                id: reservation.id,
                title: reservation.title,
                start: start,
                end: end,
                data: reservation.data,
            };
        });
    };

    const handleEventClick = (event) => {
        setSelectedReservation(event.data);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedReservation(null);
    };

    const handleEdit = () => {
        setEditModalOpen(true);
    };

    const handleDelete = async () => {
        if (selectedReservation) {
            try {
                const token = sessionStorage.getItem('authToken');
                await axiosInstance.delete(`/reservations/${selectedReservation.reservationId}`)
                handleCloseModal();
            } catch (error) {
                console.error('Error deleting reservation:', error);
            }
        }
    };

    const handleAddReservation = () => {
        setNewReservationModalOpen(true);
    };

    const handleCloseNewReservationModal = () => {
        setNewReservationModalOpen(false);
    };

    const handleCreateReservation = async (newReservation) => {
        const token = sessionStorage.getItem('authToken');
        try {
            const response = await axiosInstance.post('/reservations/create', newReservation,)
            handleCloseNewReservationModal();
        } catch (error) {
            console.error('Error creating reservation:', error);
        }
    };

    const handleSaveEdit = async (updatedReservation) => {
        const token = sessionStorage.getItem('authToken');
        try {
            const response = await axiosInstance.put(`/reservations/${updatedReservation.reservationId}`, updatedReservation)
            setEditModalOpen(false);
            setSelectedReservation(updatedReservation);
        } catch (error) {
            console.error('Error updating reservation:', error);
        }
    };

    return (
        <div className="container-fluid p-3">
            <div className style={{width: '93vw', height: '80vh'}}>
                <div className="calendar-wrapper" style={{ width: '100%', height: '100%' }}>
                    <Calendar
                        localizer={localizer}
                        events={formatReservations(reservations)} // Gebruik de geformatteerde reserveringen
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: '100%' }}
                        culture="nl"
                        onSelectEvent={handleEventClick}
                        views={['month', 'agenda']} // Only month and agenda views
                        messages={{
                            next: "Volgende",
                            previous: "Vorige",
                            today: "Vandaag",
                            month: "Maand",
                            week: "Week",
                            day: "Dag",
                            agenda: "Agenda",
                            date: "Datum",
                            time: "Tijd",
                            event: "Evenement",
                            showMore: (total) => `+${total} meer`,
                        }}
                        formats={{
                            eventTimeRangeFormat: ({ start, end }) => `${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`,
                        }}
                    />
                </div>
            </div>

            {/* Round button for adding new reservation */}
            <button className="btn add-reservation-btn" onClick={handleAddReservation}>
                +
            </button>

            {/* Modale voor reserveringsdetails */}
            {modalOpen && (
                <div className="modal fade show" style={{ display: 'block' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Reserveringsdetails</h5>
                                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                            </div>
                            <div className="modal-body">
                                {selectedReservation && (
                                    <div>
                                        <p>
                                            <strong>Klant:</strong> {selectedReservation.customer.firstName} {selectedReservation.customer.lastName}
                                        </p>
                                        <p><strong>Email:</strong> {selectedReservation.customer.email}</p>
                                        <p><strong>Telefoonnummer:</strong> {selectedReservation.customer.phoneNumber}</p>
                                        <p>
                                            <strong>Datum:</strong> {format(new Date(selectedReservation.reservationDate), 'dd MMM yyyy')}
                                        </p>
                                        <p>
                                            <strong>Tijd:</strong> {format(new Date(`${selectedReservation.reservationDate}T${selectedReservation.reservationTime}`), 'HH:mm')}
                                        </p>
                                        <p><strong>Aantal Personen:</strong> {selectedReservation.numberOfGuests}</p>
                                        <p><strong>Speciale Verzoeken:</strong> {selectedReservation.specialRequests || 'Geen'}</p>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-primary" onClick={handleEdit}>Bewerken</button>
                                <button className="btn btn-danger" onClick={handleDelete}>Verwijderen</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Nieuwe reserveringsmodale */}
            {newReservationModalOpen && (
                <NewReservationModal
                    onClose={handleCloseNewReservationModal}
                    onCreate={handleCreateReservation}
                    defaultCompanyName={companyName}
                    timeSlots={timeSlots}
                />
            )}

            {/* Bewerkingsmodale */}
            {editModalOpen && selectedReservation && (
                <EditReservationModal
                    reservation={selectedReservation}
                    onClose={() => setEditModalOpen(false)}
                    onSave={handleSaveEdit}
                    timeSlots={timeSlots}
                />
            )}
        </div>
    );
};

export default Reservations;
